import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FlagWave from "./FlagWave";
import RippleButton from "../buttons/RippleButton";
import CountryInputRow from "./CountryInputRow";
import { motion, useAnimation } from "framer-motion";

const CountryInputForm = ({ rows, results, value, onChange, onGuess }) => {
  const { t } = useTranslation();

  const [visible, setVisible] = useState(false);

  const handleGuess = () => {
    const matched = rows.find(
      (element) => element.name.toUpperCase() === value.toUpperCase(),
    );
    if (matched) {
      if (results.map((item) => item.Alpha2Code).includes(matched.Alpha2Code)) {
        toast.error(
          `${t("You already guessed", { country: matched.name.toUpperCase() })}`,
        );
      } else {
        onGuess(matched);
      }

      onChange("");
      setVisible(false);
    }
    return !matched;
  };

  const handleChange = (e) => {
    setVisible(true);
    onChange(e.target.value.toUpperCase());
  };

  const handleListItemClick = (element) => {
    setVisible(false);
    onChange(element.name.toUpperCase());
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && value !== "") {
      let notMatched = handleGuess();
      if (notMatched) {
        if (filteredRows.length > 0) {
          setVisible(false);
          onChange(filteredRows[0].name.toUpperCase());
        }
      }
    }
  };

  const filteredRows = useCallback(() => {
    return rows
      ?.filter((element) => element.name.toUpperCase().indexOf(value) > -1)
      .sort(
        (a, b) =>
          a.name.toUpperCase().indexOf(value) -
          b.name.toUpperCase().indexOf(value),
      );
  }, [value, rows])();

  useEffect(() => {
    const handleClickOutside = () => {
      setVisible(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [setVisible]);

  const selectedRow = rows.find(
    (element) => element.name.toUpperCase() === value.toUpperCase(),
  );

  const controls = useAnimation();

  useEffect(() => {
    if (visible)
      controls.start({
        opacity: 1,
        height: Math.min(200, filteredRows.length * 36),
      });
    else controls.start({ opacity: 0, height: 0 });
  }, [controls, visible, filteredRows.length]);

  return (
    <div className="relative flex h-full w-full flex-row space-x-2">
      <div className="h-full w-full  flex-auto rounded">
        <motion.ul
          initial={{ opacity: 0, height: 0 }}
          animate={controls}
          className={` absolute bottom-10 z-[99] my-0.5 w-full overflow-auto rounded-md border border-slate-800 bg-slate-300 dark:border-slate-100 dark:bg-slate-900 `}
        >
          {filteredRows?.map((element) => (
            <CountryInputRow
              key={`country-${element.Alpha2Code}`}
              element={element}
              value={value}
              handleListItemClick={handleListItemClick}
            />
          ))}
        </motion.ul>

        <div className="flex w-full flex-row">
          <span className="basis-12 rounded border-2 border-transparent bg-slate-100 px-1 outline-none transition-colors duration-300 dark:bg-slate-800 dark:text-slate-100 max-xs:hidden">
            {selectedRow && (
              <FlagWave
                imageUrl={`/data/${selectedRow?.Alpha2Code?.toLowerCase()}/flag.svg`}
                className={"w-8"}
                autoplay={true}
              />
            )}
          </span>
          <input
            type="text"
            autoComplete="off"
            className="mx-1 grow rounded border-2 border-transparent bg-white p-1 px-2 outline-none transition-colors duration-300
          hover:border-majky-400 hover:bg-majky-100  focus:border-majky-500 focus:ring focus:ring-majky-500/20 dark:bg-slate-800 dark:text-slate-100"
            placeholder={t("Country, territory...")}
            value={value}
            onChange={handleChange}
            onKeyDown={onKeyDown}
            onClick={(e) => {
              setVisible(true);
              e.stopPropagation();
            }}
          />
          <RippleButton
            className="flex h-full basis-24 items-center justify-center rounded border-2 border-transparent bg-white p-1 px-5 font-bold outline-none transition-colors duration-300 hover:border-slate-400 hover:bg-majky-100 focus:border-majky-500 focus:ring focus:ring-majky-500/20 active:bg-white disabled:bg-slate-50  disabled:text-slate-300 dark:bg-slate-800 dark:text-slate-100
              dark:hover:bg-majky-600 hover:dark:bg-slate-700 dark:active:bg-slate-600 disabled:dark:border-slate-500 disabled:dark:bg-slate-800 disabled:dark:text-slate-500 "
            onClick={() => {
              if (handleGuess() && value !== "")
                toast.error(t("Unknown Country!"), { position: "top-center" });
            }}
          >
            <img
              alt="🌎"
              src="/images/1f30e.png"
              className="mr-0.5 h-4 w-4 max-xs:hidden"
            />
            <span>{t("Guess")}</span>
          </RippleButton>
        </div>
      </div>
    </div>
  );
};

export default CountryInputForm;
